// NO "use client" — deliberate. These section components are pure (no hooks,
// no handlers, no browser APIs), so they must stay renderable from BOTH React
// layers. apps/web renders them from server components (`page.tsx`) so the
// legal text lands in the server-rendered HTML — crawlers, OAuth provider
// verification bots (Google/Apple/Facebook fetch the privacy-policy URL) and
// no-JS visitors all read the document without executing a line of JS. A
// "use client" directive here would turn every section into a client
// reference whose `t` function prop cannot cross the RSC boundary, forcing
// the text back behind hydration. Client hosts (legal-shell carousel
// neighbours, legal-modal, apps/app) import them just the same — a module
// without the directive is bundled into whichever graph imports it.
//
// Originally lifted from apps/web's port of legacy
// src/app/[locale]/[country]/legal/sections.tsx. Now lives in @pontimed/ui-legal
// (P12) so apps/web (next-intl) and apps/app (Lingui) can both render legal
// content without coupling to either i18n library.
//
// Each section accepts a `t: (key: string) => string` prop. Hosts build it
// via `getLegalT(locale)` from "@pontimed/ui-legal/i18n" — the package owns
// the i18n catalogs (8 locales) under `messages/legal-{locale}.json`.
//
// lucide-react replaced with minimal local SVG stubs — keeps the legal pages
// dependency-free aside from React. V1.1 packages/ui-marketing extraction
// will reintroduce icons via the shared set.

/**
 * Translator signature accepted by section components and DpaBlock. Hosts
 * obtain it via `getLegalT(locale)` from "@pontimed/ui-legal/i18n" — the
 * package owns the catalogs and supports `{name}` placeholder substitution.
 */
export type LegalTranslator = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Action targets that differ per host. A missing field keeps the package
 * default, so hosts without their own routing (apps/app's legal sheet) need
 * not pass anything.
 */
export interface LegalLinks {
  /** DSA notice-and-action form. Default: the production URL. apps/web passes
   *  its locale/country-aware path so the visitor keeps language and host. */
  reportContent?: string;
  /** Where a signed-in user deletes the account, with the CTA label (the
   *  legal catalog has no string for it). Omitted → no CTA. */
  deleteAccount?: { href: string; label: string };
  /** Control that reopens the host's cookie-consent banner, shown under the
   *  cookie policy's "managing cookies" section. Omitted → nothing (apps/app
   *  has no banner to reopen). */
  cookieSettings?: React.ReactNode;
  /** Where a listed professional finds and claims their profile. Default: the
   *  production URL. apps/web passes its locale/country-aware path. */
  claimProfile?: string;
}

/** Per-host presentation of one legal document (see `renderLegalSection`). */
export interface LegalSectionOptions {
  /** Level of the document title. Default `"h2"` — modal and sheet hosts sit
   *  under their own heading. A page whose only h1 is the document passes
   *  `"h1"`. */
  titleAs?: "h1" | "h2";
  /** Host typography for the title. When set it replaces the prose heading
   *  styles (`not-prose`), so it must carry size, weight, colour and margin. */
  titleClassName?: string;
  links?: LegalLinks;
}

type LegalSectionProps = { t: LegalTranslator } & LegalSectionOptions;

/** Production DSA form, for hosts that pass no `links.reportContent`. */
const DEFAULT_REPORT_CONTENT_URL = "https://pontimed.app/legal/report-content";

/** Production claim page, for hosts that pass no `links.claimProfile`. */
const DEFAULT_CLAIM_PROFILE_URL = "https://pontimed.app/claim";

function DocTitle({
  as: Tag = "h2",
  className,
  children,
}: {
  as?: "h1" | "h2";
  className?: string;
  children: React.ReactNode;
}) {
  return <Tag className={className ? `not-prose ${className}` : undefined}>{children}</Tag>;
}

/**
 * Stable anchor for a section heading, derived from its catalog key rather
 * than its text so it survives copy edits and is the same in every locale:
 * `"dataProtection.s5Title"` → `"data-protection-s5"`. Lets the portal, emails
 * and support replies deep-link a section (`/legal/terms#terms-s5`).
 */
function sectionAnchor(key: string): string {
  return key
    .replace(/Title$/, "")
    .replace(".", "-")
    .replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/**
 * Heading levels under the document title, one step below it at every depth
 * so the outline never skips a level: title h1 → sections h2 → sub-headings
 * h3 (apps/web pages); title h2 → h3 → h4 (modal and sheet hosts).
 */
function headingLevels(titleAs: "h1" | "h2" = "h2") {
  return titleAs === "h1"
    ? ({ section: "h2", sub: "h3" } as const)
    : ({ section: "h3", sub: "h4" } as const);
}

/**
 * Numbered section heading. Styled explicitly (`not-prose`) instead of via a
 * `prose-h3:` modifier so it looks the same at either level: the carousel
 * swaps an h1-titled page panel for its h2-titled neighbour mid-swipe.
 * `scroll-mt-28` clears the legal pages' pinned chrome (logo row + tab
 * strip) when the browser jumps to the anchor.
 */
function SectionHeading({ t, k, as: Tag }: { t: LegalTranslator; k: string; as: "h2" | "h3" }) {
  return (
    <Tag
      id={sectionAnchor(k)}
      className="not-prose font-display text-foreground mt-10 mb-3 scroll-mt-28 text-lg leading-snug font-semibold tracking-[-0.01em] sm:text-xl"
    >
      {t(k)}
    </Tag>
  );
}

/** Sub-heading inside a section (cookie categories, the DPO block). */
function SubHeading({ as: Tag, children }: { as: "h3" | "h4"; children: React.ReactNode }) {
  return (
    <Tag className="not-prose font-display text-foreground mt-6 mb-2 leading-[1.43] font-semibold">
      {children}
    </Tag>
  );
}

/**
 * The one action a document exists for (report content, delete the account),
 * as a filled button instead of a text link lost in the prose. Same fill as
 * the hosts' primary buttons (`primary-fill`: the label reaches AA in both
 * themes).
 */
function LegalCta({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <p className="not-prose my-6">
      <a
        href={href}
        className="bg-primary-fill text-primary-foreground hover:bg-primary-fill-hover focus-visible:ring-ring inline-flex min-h-11 items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {children}
      </a>
    </p>
  );
}

/**
 * Two actions of equal standing, side by side from sm up and stacked on
 * phones: the primary fill for the first, an outlined button for the second.
 * Used where a document offers a choice (claim the profile / have it removed)
 * and neither may be buried in the prose.
 */
function LegalCtaPair({
  primary,
  secondary,
}: {
  primary: { href: string; label: string };
  secondary: { href: string; label: string };
}) {
  return (
    <div className="not-prose my-6 flex flex-col gap-3 sm:flex-row">
      <a
        href={primary.href}
        className="bg-primary-fill text-primary-foreground hover:bg-primary-fill-hover focus-visible:ring-ring inline-flex min-h-11 items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {primary.label}
      </a>
      <a
        href={secondary.href}
        className="border-border bg-card text-foreground hover:bg-muted focus-visible:ring-ring inline-flex min-h-11 items-center justify-center rounded-lg border px-5 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        {secondary.label}
      </a>
    </div>
  );
}

type IconProps = { className?: string; size?: number; strokeWidth?: number };
function svg(path: React.ReactNode) {
  // eslint-disable-next-line react/display-name
  return ({ className, size = 24, strokeWidth = 2 }: IconProps) => (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

const Shield = svg(
  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />,
);
const Lock = svg(
  <>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </>,
);
const Server = svg(
  <>
    <rect width="20" height="8" x="2" y="2" rx="2" ry="2" />
    <rect width="20" height="8" x="2" y="14" rx="2" ry="2" />
    <line x1="6" x2="6.01" y1="6" y2="6" />
    <line x1="6" x2="6.01" y1="18" y2="18" />
  </>,
);
const Eye = svg(
  <>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
const Key = svg(
  <>
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="m21 2-9.6 9.6" />
    <path d="m15.5 7.5 3 3L22 7l-3-3" />
  </>,
);
const FileCheck = svg(
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="m9 15 2 2 4-4" />
  </>,
);
const Globe = svg(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </>,
);
const FileJson = svg(
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M10 13a1 1 0 1 0-2 0v3a1 1 0 0 1-2 0" />
    <path d="M14 13a1 1 0 1 1 2 0v3a1 1 0 0 0 2 0" />
  </>,
);
const UserCheck = svg(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <polyline points="16 11 18 13 22 9" />
  </>,
);
const Building2 = svg(
  <>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
    <path d="M10 6h4" />
    <path d="M10 10h4" />
    <path d="M10 14h4" />
    <path d="M10 18h4" />
  </>,
);
const Stethoscope = svg(
  <>
    <path d="M11 2v2" />
    <path d="M5 2v2" />
    <path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1" />
    <path d="M8 15a6 6 0 0 0 12 0v-3" />
    <circle cx="20" cy="10" r="2" />
  </>,
);

/**
 * Puls document typography, applied on every section's <article>. The hosts
 * wrap it in their `prose prose-sm` panel (apps/web legal pages + legal modal,
 * apps/app legal sheet), so these modifiers style the bare h1/h2/h3/p/ul inside:
 * display-face headings, a lead paragraph, list markers and links in primary,
 * a slightly larger reading size. One literal on purpose — Tailwind scans raw
 * text. `font-display` is a no-op in hosts without a display face (apps/app
 * keeps Inter), so the sheet still reads correctly there.
 */
const ARTICLE_CLASS =
  "text-[0.9375rem]/[1.7] sm:text-base/[1.75] marker:text-primary prose-h1:font-display prose-h1:mt-0 prose-h1:mb-3 prose-h1:text-2xl sm:prose-h1:text-[1.75rem] prose-h1:leading-tight prose-h1:font-semibold prose-h1:tracking-[-0.02em] prose-h1:text-balance prose-h2:font-display prose-h2:mt-0 prose-h2:mb-3 prose-h2:text-2xl sm:prose-h2:text-[1.75rem] prose-h2:leading-tight prose-h2:font-semibold prose-h2:tracking-[-0.02em] prose-h2:text-balance prose-lead:mt-0 prose-lead:mb-8 prose-lead:text-base sm:prose-lead:text-lg prose-lead:leading-relaxed prose-h3:font-display prose-h3:mt-10 prose-h3:mb-3 prose-h3:text-lg sm:prose-h3:text-xl prose-h3:leading-snug prose-h3:font-semibold prose-h3:tracking-[-0.01em] prose-h4:font-display prose-h4:mt-6 prose-h4:mb-2 prose-h4:font-semibold prose-a:text-primary prose-a:font-medium prose-a:underline-offset-2";

/** Feature card (security features, EHDS pillars) — Puls card with a soft
 *  primary wash at the top, outside the prose rhythm (`not-prose` grid). */
const FEATURE_CARD =
  "border-border bg-card from-primary-soft/70 to-card rounded-2xl border bg-gradient-to-b to-60% p-5 sm:p-6";
const FEATURE_ICON_TILE =
  "border-border bg-card text-primary mb-4 grid size-11 place-items-center rounded-lg border";

/** Cookie tables — bordered card, mono column headers. */
const TABLE_WRAP = "not-prose border-border my-5 overflow-x-auto rounded-2xl border";
const TABLE_HEAD_ROW = "border-border bg-muted/40 border-b";
const TABLE_TH =
  "text-foreground px-4 py-2.5 text-left font-mono text-[11px] font-medium tracking-[0.08em] uppercase";
const TABLE_ROW = "border-border border-b last:border-b-0";
const TABLE_TD_NAME = "text-foreground px-4 py-2.5 font-mono text-xs";
const TABLE_TD = "px-4 py-2.5";

/* ─────────────────────────── TERMS ─────────────────────────── */

export function TermsSection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("terms.title")}
      </DocTitle>
      <p className="lead">{t("terms.intro")}</p>

      <SectionHeading t={t} k="terms.s1Title" as={lv.section} />
      <p>{t("terms.s1p1")}</p>
      <p>{t("terms.s1p2")}</p>

      <SectionHeading t={t} k="terms.s2Title" as={lv.section} />
      <p>{t("terms.s2p1")}</p>
      <ul>
        <li>{t("terms.s2l1")}</li>
        <li>{t("terms.s2l2")}</li>
        <li>{t("terms.s2l3")}</li>
        <li>{t("terms.s2l4")}</li>
      </ul>

      <SectionHeading t={t} k="terms.s3Title" as={lv.section} />
      <p>{t("terms.s3p1")}</p>
      <ul>
        <li>{t("terms.s3l1")}</li>
        <li>{t("terms.s3l2")}</li>
        <li>{t("terms.s3l3")}</li>
        <li>{t("terms.s3l4")}</li>
      </ul>

      <SectionHeading t={t} k="terms.s4Title" as={lv.section} />
      <p>{t("terms.s4p1")}</p>
      <p>{t("terms.s4p2")}</p>
      <p>{t("terms.s4p3")}</p>
      <p>
        <strong>{t("terms.s4p4")}</strong>
      </p>
      <p>{t("terms.s4p5")}</p>
      <p>{t("terms.s4p6")}</p>

      <SectionHeading t={t} k="terms.s5Title" as={lv.section} />
      <p>{t("terms.s5p1")}</p>
      <ul>
        <li>{t("terms.s5l1")}</li>
        <li>{t("terms.s5l2")}</li>
        <li>{t("terms.s5l3")}</li>
      </ul>
      <p>{t("terms.s5p2")}</p>
      <p>{t("terms.s5p3")}</p>

      <SectionHeading t={t} k="terms.s6Title" as={lv.section} />
      <p>{t("terms.s6p1")}</p>
      <p>{t("terms.s6p2")}</p>

      <SectionHeading t={t} k="terms.s7Title" as={lv.section} />
      <p>{t("terms.s7p1")}</p>
      <p>{t("terms.s7p2")}</p>

      <SectionHeading t={t} k="terms.s8Title" as={lv.section} />
      <p>{t("terms.s8p1")}</p>
      <ul>
        <li>{t("terms.s8l1")}</li>
        <li>{t("terms.s8l2")}</li>
        <li>{t("terms.s8l3")}</li>
        <li>{t("terms.s8l4")}</li>
        <li>{t("terms.s8l5")}</li>
      </ul>
      <p>{t("terms.s8p2")}</p>

      <SectionHeading t={t} k="terms.s9Title" as={lv.section} />
      <p>{t("terms.s9p1")}</p>
      <p>{t("terms.s9p2")}</p>
      <p>{t("terms.s9p3")}</p>

      <SectionHeading t={t} k="terms.s10Title" as={lv.section} />
      <p>{t("terms.s10p1")}</p>

      <SectionHeading t={t} k="terms.s11Title" as={lv.section} />
      <p>{t("terms.s11p1")}</p>
      <ul>
        <li>{t("terms.s11l1")}</li>
        <li>{t("terms.s11l2")}</li>
        <li>{t("terms.s11l3")}</li>
        <li>{t("terms.s11l4")}</li>
        <li>{t("terms.s11l5")}</li>
        <li>{t("terms.s11l6")}</li>
        <li>{t("terms.s11l7")}</li>
      </ul>

      <SectionHeading t={t} k="terms.s12Title" as={lv.section} />
      <p>{t("terms.s12p1")}</p>
      <p>{t("terms.s12p2")}</p>

      <SectionHeading t={t} k="terms.s13Title" as={lv.section} />
      <p>{t("terms.s13p1")}</p>

      <SectionHeading t={t} k="terms.s14Title" as={lv.section} />
      <p>{t("terms.s14p1")}</p>

      <SectionHeading t={t} k="terms.s15Title" as={lv.section} />
      <p>{t("terms.s15p1")}</p>
    </article>
  );
}

/* ─────────────────────────── PRIVACY ─────────────────────────── */

export function PrivacySection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("privacy.title")}
      </DocTitle>
      <p className="lead">{t("privacy.intro")}</p>

      <SectionHeading t={t} k="privacy.s1Title" as={lv.section} />
      <p>{t("privacy.s1p1")}</p>

      <SectionHeading t={t} k="privacy.s2Title" as={lv.section} />
      <p>{t("privacy.s2p1")}</p>
      <ul>
        <li>{t("privacy.s2l1")}</li>
        <li>{t("privacy.s2l2")}</li>
        <li>{t("privacy.s2l3")}</li>
        <li>{t("privacy.s2l4")}</li>
        <li>{t("privacy.s2l5")}</li>
      </ul>

      <SectionHeading t={t} k="privacy.s3Title" as={lv.section} />
      <p>{t("privacy.s3p1")}</p>
      <ul>
        <li>{t("privacy.s3l1")}</li>
        <li>{t("privacy.s3l2")}</li>
        <li>{t("privacy.s3l3")}</li>
        <li>{t("privacy.s3l4")}</li>
        <li>{t("privacy.s3l5")}</li>
      </ul>

      <SectionHeading t={t} k="privacy.s4Title" as={lv.section} />
      <p>{t("privacy.s4p1")}</p>
      <ul>
        <li>{t("privacy.s4l1")}</li>
        <li>{t("privacy.s4l2")}</li>
        <li>{t("privacy.s4l3")}</li>
        <li>{t("privacy.s4l4")}</li>
      </ul>

      <SectionHeading t={t} k="privacy.s5Title" as={lv.section} />
      <p>{t("privacy.s5p1")}</p>
      <ul>
        <li>{t("privacy.s5l1")}</li>
        <li>{t("privacy.s5l2")}</li>
        <li>{t("privacy.s5l3")}</li>
      </ul>

      <SectionHeading t={t} k="privacy.s6Title" as={lv.section} />
      <p>{t("privacy.s6p1")}</p>

      <SectionHeading t={t} k="privacy.s7Title" as={lv.section} />
      <p>{t("privacy.s7p1")}</p>
      <ul>
        <li>{t("privacy.s7l1")}</li>
        <li>{t("privacy.s7l2")}</li>
        <li>{t("privacy.s7l3")}</li>
        <li>{t("privacy.s7l4")}</li>
        <li>{t("privacy.s7l5")}</li>
        <li>{t("privacy.s7l6")}</li>
      </ul>

      <SectionHeading t={t} k="privacy.s8Title" as={lv.section} />
      <p>{t("privacy.s8p1")}</p>

      <SectionHeading t={t} k="privacy.s9Title" as={lv.section} />
      <p>{t("privacy.s9p1")}</p>

      <SectionHeading t={t} k="privacy.s10Title" as={lv.section} />
      <p>{t("privacy.s10p1")}</p>

      <SectionHeading t={t} k="privacy.sAiTitle" as={lv.section} />
      <p>{t("privacy.sAip1")}</p>
      <ul>
        <li>{t("privacy.sAil1")}</li>
        <li>{t("privacy.sAil2")}</li>
        <li>{t("privacy.sAil3")}</li>
        <li>{t("privacy.sAil4")}</li>
        <li>{t("privacy.sAil5")}</li>
      </ul>
      <p>{t("privacy.sAip2")}</p>
    </article>
  );
}

/* ─────────────────────────── COOKIES ─────────────────────────── */

type CookieRow = { name: string; purpose: string; duration: string };

/**
 * Inventory of the cookies the platform actually writes (Set-Cookie in
 * apps/api, `document.cookie` in apps/web + apps/app, the apps/web
 * middleware), with the lifetime each one is written with. localStorage keys
 * are not cookies and stay out. Update this list with the code.
 */
const NECESSARY_COOKIES: CookieRow[] = [
  // apps/api auth routes — access token (15 min) + rotated refresh token.
  { name: "pontimed_at", purpose: "cookieAccess", duration: "duration15m" },
  { name: "pontimed_rt", purpose: "cookieRefresh", duration: "durationRefresh" },
  // apps/api/src/lib/two-factor.ts — 24 h / 7 d / 30 d by account type.
  { name: "pm_2fa", purpose: "cookie2fa", duration: "duration2fa" },
  // apps/api/src/lib/vault-unlock.ts — the user's vaultUnlockMinutes (1–60, default 5).
  { name: "pm_vault", purpose: "cookieVault", duration: "durationVault" },
  // apps/api/src/lib/owner-proof.ts — owner proof before sensitive account changes, host-only, 1 h.
  { name: "__Host-pm_proof", purpose: "cookieOwnerProof", duration: "duration1h" },
  // apps/api/src/routes/auth-oauth.ts — CSRF state + nonce per provider;
  // lib/oauth-link-intent.ts — ties a connection from Settings to this browser.
  {
    name: "pontimed_oauth_state_*, pontimed_oauth_nonce_*, __Host-pontimed_oauth_link_intent_*",
    purpose: "cookieOauth",
    duration: "duration10m",
  },
  { name: "pontimed_oauth_link", purpose: "cookieOauthLink", duration: "duration15m" },
  // packages/analytics/src/consent.ts — 393 days.
  { name: "cookie_consent", purpose: "cookieConsent", duration: "duration13m" },
  // apps/web middleware + next-intl (session cookie).
  { name: "geo_country", purpose: "cookieGeo", duration: "duration30d" },
  { name: "pm_auto_locale", purpose: "cookieAutoLocale", duration: "duration1m" },
  { name: "NEXT_LOCALE", purpose: "cookieLocale", duration: "durationSession" },
  // Shared with the portal across *.pontimed.app.
  { name: "pontimed_theme, pontimed_locale", purpose: "cookieSharedPrefs", duration: "duration1y" },
  { name: "pontimed_country", purpose: "cookieCountry", duration: "duration1y" },
  { name: "last_country, last_locale", purpose: "cookieLastSearch", duration: "duration90d" },
  { name: "pontimed_role", purpose: "cookieRole", duration: "duration30d" },
  { name: "pontimed_ref", purpose: "cookieReferral", duration: "duration30d" },
  { name: "pm-preview", purpose: "cookiePreview", duration: "duration30d" },
];

const ANALYTICS_COOKIES: CookieRow[] = [
  { name: "ph_*_posthog", purpose: "cookiePosthogDistinct", duration: "duration1y" },
  // apps/web/src/lib/attribution/use-utm-attribution.ts — only after consent.
  { name: "pontimed_utm", purpose: "cookieUtm", duration: "duration30d" },
];

function CookieTable({ t, rows }: { t: LegalTranslator; rows: CookieRow[] }) {
  return (
    <div className={TABLE_WRAP}>
      <table className="text-muted-foreground w-full text-sm">
        <thead>
          <tr className={TABLE_HEAD_ROW}>
            <th className={`${TABLE_TH} whitespace-nowrap`}>{t("cookies.tableHeaderName")}</th>
            <th className={TABLE_TH}>{t("cookies.tableHeaderPurpose")}</th>
            <th className={TABLE_TH}>{t("cookies.tableHeaderDuration")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className={TABLE_ROW}>
              <td className={TABLE_TD_NAME}>{row.name}</td>
              <td className={TABLE_TD}>{t(`cookies.${row.purpose}`)}</td>
              <td className={TABLE_TD}>{t(`cookies.${row.duration}`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CookiesSection({ t, titleAs, titleClassName, links }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("cookies.title")}
      </DocTitle>
      <p className="lead">{t("cookies.intro")}</p>

      <SectionHeading t={t} k="cookies.s1Title" as={lv.section} />
      <p>{t("cookies.s1p1")}</p>

      <SectionHeading t={t} k="cookies.s2Title" as={lv.section} />
      <p>{t("cookies.s2p1")}</p>

      <SubHeading as={lv.sub}>{t("cookies.s2necessaryTitle")}</SubHeading>
      <p>{t("cookies.s2necessaryDesc")}</p>
      <CookieTable t={t} rows={NECESSARY_COOKIES} />

      <SubHeading as={lv.sub}>{t("cookies.s2analyticsTitle")}</SubHeading>
      <p>{t("cookies.s2analyticsDesc")}</p>
      <CookieTable t={t} rows={ANALYTICS_COOKIES} />

      <SectionHeading t={t} k="cookies.s3Title" as={lv.section} />
      <p>{t("cookies.s3p1")}</p>
      {/* Host control that reopens its consent banner (apps/web); a host
          without a banner passes nothing. */}
      {links?.cookieSettings && <div className="not-prose my-5">{links.cookieSettings}</div>}

      <SectionHeading t={t} k="cookies.s4Title" as={lv.section} />
      <p>{t("cookies.s4p1")}</p>

      <SectionHeading t={t} k="cookies.s5Title" as={lv.section} />
      <p>{t("cookies.s5p1")}</p>
    </article>
  );
}

/* ─────────────────────────── GDPR ─────────────────────────── */

export function GdprSection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("gdpr.title")}
      </DocTitle>
      <p className="lead">{t("gdpr.intro")}</p>

      <SectionHeading t={t} k="gdpr.s1Title" as={lv.section} />
      <p>{t("gdpr.s1p1")}</p>

      <SectionHeading t={t} k="gdpr.s2Title" as={lv.section} />
      <p>{t("gdpr.s2p1")}</p>
      <ul>
        <li>
          <strong>{t("gdpr.s2r1Label")}</strong> — {t("gdpr.s2r1Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r2Label")}</strong> — {t("gdpr.s2r2Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r3Label")}</strong> — {t("gdpr.s2r3Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r4Label")}</strong> — {t("gdpr.s2r4Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r5Label")}</strong> — {t("gdpr.s2r5Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r6Label")}</strong> — {t("gdpr.s2r6Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s2r7Label")}</strong> — {t("gdpr.s2r7Desc")}
        </li>
      </ul>

      <SectionHeading t={t} k="gdpr.s3Title" as={lv.section} />
      <p>{t("gdpr.s3p1")}</p>
      <ul>
        <li>{t("gdpr.s3l1")}</li>
        <li>{t("gdpr.s3l2")}</li>
        <li>{t("gdpr.s3l3")}</li>
      </ul>

      <SectionHeading t={t} k="gdpr.s4Title" as={lv.section} />
      <p>{t("gdpr.s4p1")}</p>
      <ul>
        <li>{t("gdpr.s4l1")}</li>
        <li>{t("gdpr.s4l2")}</li>
        <li>{t("gdpr.s4l3")}</li>
      </ul>

      <SectionHeading t={t} k="gdpr.s5Title" as={lv.section} />
      <p>{t("gdpr.s5p1")}</p>
      <ul>
        <li>
          <strong>{t("gdpr.s5l1Label")}</strong> — {t("gdpr.s5l1Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s5l2Label")}</strong> — {t("gdpr.s5l2Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s5l3Label")}</strong> — {t("gdpr.s5l3Desc")}
        </li>
      </ul>

      <SectionHeading t={t} k="gdpr.s6Title" as={lv.section} />
      <p>{t("gdpr.s6p1")}</p>

      <SectionHeading t={t} k="gdpr.s7Title" as={lv.section} />
      <p>{t("gdpr.s7p1")}</p>

      <SectionHeading t={t} k="gdpr.s8Title" as={lv.section} />
      <p>{t("gdpr.s8p1")}</p>
      <p>{t("gdpr.s8p2")}</p>

      <SubHeading as={lv.sub}>{t("gdpr.s8DpoTitle")}</SubHeading>
      <p>{t("gdpr.s8DpoIntro")}</p>
      <ul>
        <li>{t("gdpr.s8DpoName")}</li>
        <li>
          <a href={`mailto:${t("gdpr.s8DpoEmail")}`}>{t("gdpr.s8DpoEmail")}</a>
        </li>
        <li>{t("gdpr.s8DpoAddressNote")}</li>
      </ul>
      <p>{t("gdpr.s8DpoSLA")}</p>

      <SectionHeading t={t} k="gdpr.s9Title" as={lv.section} />
      <p>{t("gdpr.s9p1")}</p>
      <ul>
        <li>{t("gdpr.s9l1")}</li>
        <li>{t("gdpr.s9l2")}</li>
        <li>{t("gdpr.s9l3")}</li>
        <li>{t("gdpr.s9l4")}</li>
        <li>{t("gdpr.s9l5")}</li>
      </ul>
      <p>{t("gdpr.s9p2")}</p>

      <SectionHeading t={t} k="gdpr.s10Title" as={lv.section} />
      <p>{t("gdpr.s10p1")}</p>
      <ul>
        <li>
          <strong>{t("gdpr.s10l1Label")}</strong> — {t("gdpr.s10l1Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s10l2Label")}</strong> — {t("gdpr.s10l2Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s10l3Label")}</strong> — {t("gdpr.s10l3Desc")}
        </li>
        <li>
          <strong>{t("gdpr.s10l4Label")}</strong> — {t("gdpr.s10l4Desc")}
        </li>
      </ul>
      <p>{t("gdpr.s10p2")}</p>
    </article>
  );
}

/* ─────────────────────────── SECURITY ─────────────────────────── */

export function SecuritySection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  const features = [
    {
      icon: Lock,
      titleKey: "security.feat1Title" as const,
      descKey: "security.feat1Desc" as const,
    },
    {
      icon: Server,
      titleKey: "security.feat2Title" as const,
      descKey: "security.feat2Desc" as const,
    },
    { icon: Eye, titleKey: "security.feat3Title" as const, descKey: "security.feat3Desc" as const },
    { icon: Key, titleKey: "security.feat4Title" as const, descKey: "security.feat4Desc" as const },
    {
      icon: Shield,
      titleKey: "security.feat5Title" as const,
      descKey: "security.feat5Desc" as const,
    },
    {
      icon: FileCheck,
      titleKey: "security.feat6Title" as const,
      descKey: "security.feat6Desc" as const,
    },
  ];

  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("security.title")}
      </DocTitle>
      <p className="not-prose font-display text-primary mt-4 mb-6 text-xl leading-snug font-medium tracking-[-0.01em] text-balance sm:text-2xl">
        &ldquo;{t("security.tagline")}&rdquo;
      </p>
      <p className="lead">{t("security.intro")}</p>

      <div className="not-prose mt-8 grid gap-4 sm:grid-cols-2">
        {features.map(({ icon: Icon, titleKey, descKey }) => (
          <div key={titleKey} className={FEATURE_CARD}>
            <div className={FEATURE_ICON_TILE}>
              <Icon className="size-5" />
            </div>
            <lv.section className="font-display text-foreground m-0 mb-2 text-lg leading-snug font-semibold tracking-[-0.01em]">
              {t(titleKey)}
            </lv.section>
            <p className="text-muted-foreground m-0 text-sm leading-relaxed">{t(descKey)}</p>
          </div>
        ))}
      </div>

      <SectionHeading t={t} k="security.s1Title" as={lv.section} />
      <p>{t("security.s1p1")}</p>
      <ul>
        <li>{t("security.s1l1")}</li>
        <li>{t("security.s1l2")}</li>
        <li>{t("security.s1l3")}</li>
        <li>{t("security.s1l4")}</li>
      </ul>

      <SectionHeading t={t} k="security.s2Title" as={lv.section} />
      <p>{t("security.s2p1")}</p>
      <ul>
        <li>{t("security.s2l1")}</li>
        <li>{t("security.s2l2")}</li>
        <li>{t("security.s2l3")}</li>
        <li>{t("security.s2l4")}</li>
      </ul>

      <SectionHeading t={t} k="security.s3Title" as={lv.section} />
      <p>{t("security.s3p1")}</p>
      <ul>
        <li>{t("security.s3l1")}</li>
        <li>{t("security.s3l2")}</li>
        <li>{t("security.s3l3")}</li>
      </ul>

      <SectionHeading t={t} k="security.s4Title" as={lv.section} />
      <p>{t("security.s4p1")}</p>
      <ul>
        <li>{t("security.s4l1")}</li>
        <li>{t("security.s4l2")}</li>
        <li>{t("security.s4l3")}</li>
      </ul>

      <SectionHeading t={t} k="security.s5Title" as={lv.section} />
      <p>{t("security.s5p1")}</p>

      <SectionHeading t={t} k="security.s6Title" as={lv.section} />
      <p>{t("security.s6p1")}</p>

      <SectionHeading t={t} k="security.s7Title" as={lv.section} />
      <p>{t("security.s7p1")}</p>
      <ul>
        <li>{t("security.s7l1")}</li>
        <li>{t("security.s7l2")}</li>
        <li>{t("security.s7l3")}</li>
        <li>{t("security.s7l4")}</li>
        <li>{t("security.s7l5")}</li>
        <li>{t("security.s7l6")}</li>
        <li>{t("security.s7l7")}</li>
        <li>{t("security.s7l8")}</li>
        <li>{t("security.s7l9")}</li>
        <li>{t("security.s7l10")}</li>
        <li>{t("security.s7l11")}</li>
        <li>{t("security.s7l12")}</li>
        <li>{t("security.s7l13")}</li>
      </ul>

      <SectionHeading t={t} k="security.s8Title" as={lv.section} />
      <p>{t("security.s8p1")}</p>
      <ul>
        <li>{t("security.s8l1")}</li>
        <li>{t("security.s8l2")}</li>
        <li>{t("security.s8l3")}</li>
        <li>{t("security.s8l4")}</li>
        <li>{t("security.s8l5")}</li>
      </ul>

      <SectionHeading t={t} k="security.s9Title" as={lv.section} />
      <p>{t("security.s9p1")}</p>
      <ul>
        <li>
          <strong>{t("security.s9l1Label")}</strong> — {t("security.s9l1Desc")}
        </li>
        <li>
          <strong>{t("security.s9l2Label")}</strong> — {t("security.s9l2Desc")}
        </li>
        <li>
          <strong>{t("security.s9l3Label")}</strong> — {t("security.s9l3Desc")}
        </li>
        <li>
          <strong>{t("security.s9l4Label")}</strong> — {t("security.s9l4Desc")}
        </li>
        <li>
          <strong>{t("security.s9l5Label")}</strong> — {t("security.s9l5Desc")}
        </li>
        <li>
          <strong>{t("security.s9l6Label")}</strong> — {t("security.s9l6Desc")}
        </li>
      </ul>
      <p>{t("security.s9p2")}</p>
    </article>
  );
}

/* ─────────────────────────── EHDS ─────────────────────────── */

export function EhdsSection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  const pillars = [
    {
      icon: UserCheck,
      titleKey: "ehds.pillar1Title" as const,
      descKey: "ehds.pillar1Desc" as const,
    },
    {
      icon: Stethoscope,
      titleKey: "ehds.pillar2Title" as const,
      descKey: "ehds.pillar2Desc" as const,
    },
    {
      icon: FileJson,
      titleKey: "ehds.pillar3Title" as const,
      descKey: "ehds.pillar3Desc" as const,
    },
    { icon: Globe, titleKey: "ehds.pillar4Title" as const, descKey: "ehds.pillar4Desc" as const },
    {
      icon: Building2,
      titleKey: "ehds.pillar5Title" as const,
      descKey: "ehds.pillar5Desc" as const,
    },
  ];

  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("ehds.title")}
      </DocTitle>
      <p className="lead">{t("ehds.intro")}</p>

      <SectionHeading t={t} k="ehds.whatTitle" as={lv.section} />
      <p>{t("ehds.whatP1")}</p>
      <p>{t("ehds.whatP2")}</p>

      <SectionHeading t={t} k="ehds.pillarsTitle" as={lv.section} />
      <div className="not-prose mt-4 grid gap-4 sm:grid-cols-2">
        {pillars.map(({ icon: Icon, titleKey, descKey }) => (
          <div key={titleKey} className={FEATURE_CARD}>
            <div className={FEATURE_ICON_TILE}>
              <Icon className="size-5" />
            </div>
            <lv.sub className="font-display text-foreground m-0 mb-1.5 text-base leading-snug font-semibold tracking-[-0.01em]">
              {t(titleKey)}
            </lv.sub>
            <p className="text-muted-foreground m-0 text-sm leading-relaxed">{t(descKey)}</p>
          </div>
        ))}
      </div>

      <SectionHeading t={t} k="ehds.pontimedTitle" as={lv.section} />
      <p>{t("ehds.pontimedP1")}</p>
      <ul>
        <li>{t("ehds.pontimedL1")}</li>
        <li>{t("ehds.pontimedL2")}</li>
        <li>{t("ehds.pontimedL3")}</li>
        <li>{t("ehds.pontimedL4")}</li>
        <li>{t("ehds.pontimedL5")}</li>
        <li>{t("ehds.pontimedL6")}</li>
      </ul>

      <SectionHeading t={t} k="ehds.fhirTitle" as={lv.section} />
      <p>{t("ehds.fhirP1")}</p>
      <ul>
        <li>{t("ehds.fhirL1")}</li>
        <li>{t("ehds.fhirL2")}</li>
        <li>{t("ehds.fhirL3")}</li>
        <li>{t("ehds.fhirL4")}</li>
      </ul>

      <SectionHeading t={t} k="ehds.crossBorderTitle" as={lv.section} />
      <p>{t("ehds.crossBorderP1")}</p>
      <ul>
        <li>{t("ehds.crossBorderL1")}</li>
        <li>{t("ehds.crossBorderL2")}</li>
        <li>{t("ehds.crossBorderL3")}</li>
      </ul>

      <SectionHeading t={t} k="ehds.timelineTitle" as={lv.section} />
      <p>{t("ehds.timelineP1")}</p>
      {/* The regulation's own dates (Reg. (EU) 2025/327). The key names are
          historical — each key now carries the milestone of the year shown. */}
      <ul>
        <li>
          <strong>2025:</strong> {t("ehds.timeline2024")}
        </li>
        <li>
          <strong>2027:</strong> {t("ehds.timeline2026")}
        </li>
        <li>
          <strong>2029:</strong> {t("ehds.timeline2027")}
        </li>
        <li>
          <strong>2031:</strong> {t("ehds.timeline2029")}
        </li>
      </ul>

      <SectionHeading t={t} k="ehds.patientRightsTitle" as={lv.section} />
      <p>{t("ehds.patientRightsP1")}</p>
      <ul>
        <li>{t("ehds.patientRightsL1")}</li>
        <li>{t("ehds.patientRightsL2")}</li>
        <li>{t("ehds.patientRightsL3")}</li>
        <li>{t("ehds.patientRightsL4")}</li>
      </ul>
    </article>
  );
}

/* ─────────────────────────── DATA PROTECTION ─────────────────────────── */

export function DataProtectionSection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("dataProtection.title")}
      </DocTitle>
      <p className="lead">{t("dataProtection.intro")}</p>

      <SectionHeading t={t} k="dataProtection.s1Title" as={lv.section} />
      <p>{t("dataProtection.s1p1")}</p>
      <ul>
        <li>
          <strong>{t("dataProtection.s1l1Label")}</strong> — {t("dataProtection.s1l1Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s1l2Label")}</strong> — {t("dataProtection.s1l2Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s1l3Label")}</strong> — {t("dataProtection.s1l3Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s1l4Label")}</strong> — {t("dataProtection.s1l4Desc")}
        </li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s2Title" as={lv.section} />
      <p>{t("dataProtection.s2p1")}</p>
      <ul>
        <li>{t("dataProtection.s2l1")}</li>
        <li>{t("dataProtection.s2l2")}</li>
        <li>{t("dataProtection.s2l3")}</li>
        <li>{t("dataProtection.s2l4")}</li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s3Title" as={lv.section} />
      <p>{t("dataProtection.s3p1")}</p>
      <ul>
        <li>{t("dataProtection.s3l1")}</li>
        <li>{t("dataProtection.s3l2")}</li>
        <li>{t("dataProtection.s3l3")}</li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s4Title" as={lv.section} />
      <p>{t("dataProtection.s4p1")}</p>
      <ul>
        <li>
          <strong>{t("dataProtection.s4l1Label")}</strong> — {t("dataProtection.s4l1Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s4l2Label")}</strong> — {t("dataProtection.s4l2Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s4l3Label")}</strong> — {t("dataProtection.s4l3Desc")}
        </li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s5Title" as={lv.section} />
      <p>{t("dataProtection.s5p1")}</p>
      <ul>
        <li>{t("dataProtection.s5l1")}</li>
        <li>{t("dataProtection.s5l2")}</li>
        <li>{t("dataProtection.s5l4")}</li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s6Title" as={lv.section} />
      <p>{t("dataProtection.s6p1")}</p>

      <SectionHeading t={t} k="dataProtection.s7Title" as={lv.section} />
      <p>{t("dataProtection.s7p1")}</p>
      <ul>
        <li>{t("dataProtection.s7l1")}</li>
        <li>{t("dataProtection.s7l2")}</li>
        <li>{t("dataProtection.s7l3")}</li>
        <li>{t("dataProtection.s7l4")}</li>
        <li>{t("dataProtection.s7l5")}</li>
        <li>{t("dataProtection.s7l6")}</li>
        <li>{t("dataProtection.s7l7")}</li>
        <li>{t("dataProtection.s7l8")}</li>
        <li>{t("dataProtection.s7l9")}</li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s8Title" as={lv.section} />
      <p>{t("dataProtection.s8p1")}</p>

      <SectionHeading t={t} k="dataProtection.s9Title" as={lv.section} />
      <p>{t("dataProtection.s9p1")}</p>
      <ul>
        <li>
          <strong>{t("dataProtection.s9l1Label")}</strong> — {t("dataProtection.s9l1Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s9l2Label")}</strong> — {t("dataProtection.s9l2Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s9l3Label")}</strong> — {t("dataProtection.s9l3Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s9l4Label")}</strong> — {t("dataProtection.s9l4Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s9l5Label")}</strong> — {t("dataProtection.s9l5Desc")}
        </li>
        <li>
          <strong>{t("dataProtection.s9l6Label")}</strong> — {t("dataProtection.s9l6Desc")}
        </li>
      </ul>

      <SectionHeading t={t} k="dataProtection.s10Title" as={lv.section} />
      <p>{t("dataProtection.s10p1")}</p>
      <ul>
        <li>{t("dataProtection.s10l1")}</li>
        <li>{t("dataProtection.s10l2")}</li>
        <li>{t("dataProtection.s10l3")}</li>
        <li>{t("dataProtection.s10l4")}</li>
      </ul>
      <p>{t("dataProtection.s10p2")}</p>
      <p>{t("dataProtection.s10p3")}</p>
    </article>
  );
}

/* ─────────────────────────── DATA DELETION ─────────────────────────── */

export function DataDeletionSection({ t, titleAs, titleClassName, links }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("dataDeletion.title")}
      </DocTitle>
      <p className="lead">{t("dataDeletion.intro")}</p>

      <SectionHeading t={t} k="dataDeletion.s1Title" as={lv.section} />
      <p>{t("dataDeletion.s1p1")}</p>
      <ul>
        <li>{t("dataDeletion.s1l1")}</li>
        <li>{t("dataDeletion.s1l2")}</li>
        <li>{t("dataDeletion.s1l3")}</li>
      </ul>

      <SectionHeading t={t} k="dataDeletion.s2Title" as={lv.section} />
      <p>{t("dataDeletion.s2p1")}</p>
      <ol>
        <li>{t("dataDeletion.s2step1")}</li>
        <li>{t("dataDeletion.s2step2")}</li>
        <li>{t("dataDeletion.s2step3")}</li>
        <li>{t("dataDeletion.s2step4")}</li>
      </ol>
      {links?.deleteAccount && (
        <LegalCta href={links.deleteAccount.href}>{links.deleteAccount.label}</LegalCta>
      )}

      <SectionHeading t={t} k="dataDeletion.s3Title" as={lv.section} />
      <p>{t("dataDeletion.s3p1")}</p>

      <SectionHeading t={t} k="dataDeletion.s4Title" as={lv.section} />
      <p>{t("dataDeletion.s4p1")}</p>
      <ul>
        <li>{t("dataDeletion.s4l1")}</li>
        <li>{t("dataDeletion.s4l2")}</li>
        <li>{t("dataDeletion.s4l3")}</li>
        <li>{t("dataDeletion.s4l4")}</li>
      </ul>

      <SectionHeading t={t} k="dataDeletion.s5Title" as={lv.section} />
      <p>{t("dataDeletion.s5p1")}</p>
      <ul>
        <li>
          <strong>Google:</strong> {t("dataDeletion.s5google")}
        </li>
        <li>
          <strong>Apple:</strong> {t("dataDeletion.s5apple")}
        </li>
        <li>
          <strong>Facebook:</strong> {t("dataDeletion.s5facebook")}
        </li>
      </ul>
      <p>{t("dataDeletion.s5p2")}</p>

      <SectionHeading t={t} k="dataDeletion.s6Title" as={lv.section} />
      <p>{t("dataDeletion.s6p1")}</p>
      <ul>
        <li>{t("dataDeletion.s6l1")}</li>
        <li>{t("dataDeletion.s6l2")}</li>
        <li>{t("dataDeletion.s6l3")}</li>
      </ul>

      <SectionHeading t={t} k="dataDeletion.s7Title" as={lv.section} />
      <p>{t("dataDeletion.s7p1")}</p>
    </article>
  );
}

/* ─────────────────────── LISTED PROFESSIONALS ─────────────────────── */

/**
 * GDPR Art. 14 notice for professionals whose profile was imported from
 * public sources (`Provider.listingType = "directory"`). Published instead of
 * writing to each of them, per Art. 14(5)(b) — every unclaimed profile links
 * here. It names source CATEGORIES only: the exact source never appears
 * publicly (it stays in `data_sources`) and is given on an access request.
 */
export function ListedProfessionalsSection({
  t,
  titleAs,
  titleClassName,
  links,
}: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  const removalHref =
    `mailto:${t("listedProfessionals.dpoEmail")}` +
    `?subject=${encodeURIComponent(t("listedProfessionals.removalSubject"))}` +
    `&body=${encodeURIComponent(t("listedProfessionals.removalBody"))}`;
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("listedProfessionals.title")}
      </DocTitle>
      <p className="lead">{t("listedProfessionals.intro")}</p>
      <LegalCtaPair
        primary={{
          href: links?.claimProfile ?? DEFAULT_CLAIM_PROFILE_URL,
          label: t("listedProfessionals.ctaClaim"),
        }}
        secondary={{ href: removalHref, label: t("listedProfessionals.ctaRemove") }}
      />

      <SectionHeading t={t} k="listedProfessionals.s1Title" as={lv.section} />
      <p>{t("listedProfessionals.s1p1")}</p>

      <SectionHeading t={t} k="listedProfessionals.s2Title" as={lv.section} />
      <p>{t("listedProfessionals.s2p1")}</p>
      <ul>
        <li>{t("listedProfessionals.s2l1")}</li>
        <li>{t("listedProfessionals.s2l2")}</li>
        <li>{t("listedProfessionals.s2l3")}</li>
        <li>{t("listedProfessionals.s2l4")}</li>
        <li>{t("listedProfessionals.s2l5")}</li>
      </ul>
      <p>{t("listedProfessionals.s2p2")}</p>

      <SectionHeading t={t} k="listedProfessionals.s3Title" as={lv.section} />
      <ul>
        <li>
          <strong>{t("listedProfessionals.s3l1Label")}</strong> —{" "}
          {t("listedProfessionals.s3l1Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s3l2Label")}</strong> —{" "}
          {t("listedProfessionals.s3l2Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s3l3Label")}</strong> —{" "}
          {t("listedProfessionals.s3l3Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s3l4Label")}</strong> —{" "}
          {t("listedProfessionals.s3l4Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s3l5Label")}</strong> —{" "}
          {t("listedProfessionals.s3l5Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s3l6Label")}</strong> —{" "}
          {t("listedProfessionals.s3l6Desc")}
        </li>
      </ul>
      <p>{t("listedProfessionals.s3p1")}</p>

      <SectionHeading t={t} k="listedProfessionals.s4Title" as={lv.section} />
      <ul>
        <li>
          <strong>{t("listedProfessionals.s4l1Label")}</strong> —{" "}
          {t("listedProfessionals.s4l1Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s4l2Label")}</strong> —{" "}
          {t("listedProfessionals.s4l2Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s4l3Label")}</strong> —{" "}
          {t("listedProfessionals.s4l3Desc")}
        </li>
      </ul>
      <p>{t("listedProfessionals.s4p1")}</p>

      <SectionHeading t={t} k="listedProfessionals.s5Title" as={lv.section} />
      <p>{t("listedProfessionals.s5p1")}</p>
      <p>{t("listedProfessionals.s5p2")}</p>

      <SectionHeading t={t} k="listedProfessionals.s6Title" as={lv.section} />
      <ul>
        <li>{t("listedProfessionals.s6l1")}</li>
        <li>{t("listedProfessionals.s6l2")}</li>
        <li>{t("listedProfessionals.s6l3")}</li>
      </ul>
      <p>{t("listedProfessionals.s6p1")}</p>

      <SectionHeading t={t} k="listedProfessionals.s7Title" as={lv.section} />
      <p>{t("listedProfessionals.s7p1")}</p>
      <p>{t("listedProfessionals.s7p2")}</p>

      <SectionHeading t={t} k="listedProfessionals.s8Title" as={lv.section} />
      <p>{t("listedProfessionals.s8p1")}</p>
      <ul>
        <li>{t("listedProfessionals.s8l1")}</li>
        <li>{t("listedProfessionals.s8l2")}</li>
        <li>{t("listedProfessionals.s8l3")}</li>
        <li>{t("listedProfessionals.s8l4")}</li>
        <li>
          <strong>{t("listedProfessionals.s8l5")}</strong>
        </li>
      </ul>
      <p>{t("listedProfessionals.s8p2")}</p>
      <p>{t("listedProfessionals.s8p3")}</p>

      <SectionHeading t={t} k="listedProfessionals.s9Title" as={lv.section} />
      <ul>
        <li>
          <strong>{t("listedProfessionals.s9l1Label")}</strong> —{" "}
          {t("listedProfessionals.s9l1Desc")}
        </li>
        <li>
          <strong>{t("listedProfessionals.s9l2Label")}</strong> —{" "}
          {t("listedProfessionals.s9l2Desc")}{" "}
          <a href={removalHref}>{t("listedProfessionals.dpoEmail")}</a>
        </li>
        <li>
          <strong>{t("listedProfessionals.s9l3Label")}</strong> —{" "}
          {t("listedProfessionals.s9l3Desc")}
        </li>
      </ul>
    </article>
  );
}

/* ─────────────────────────── PARTNERSHIP ─────────────────────────── */

export function PartnershipSection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("partnership.title")}
      </DocTitle>
      <p className="lead">{t("partnership.intro")}</p>

      <SectionHeading t={t} k="partnership.s1Title" as={lv.section} />
      <p>{t("partnership.s1p1")}</p>
      <p>{t("partnership.s1p2")}</p>

      <SectionHeading t={t} k="partnership.s2Title" as={lv.section} />
      <p>{t("partnership.s2p1")}</p>
      <ul>
        <li>{t("partnership.s2l1")}</li>
        <li>{t("partnership.s2l2")}</li>
        <li>{t("partnership.s2l3")}</li>
        <li>{t("partnership.s2l4")}</li>
      </ul>

      <SectionHeading t={t} k="partnership.s3Title" as={lv.section} />
      <p>{t("partnership.s3p1")}</p>
      <ul>
        <li>{t("partnership.s3l1")}</li>
        <li>{t("partnership.s3l2")}</li>
        <li>{t("partnership.s3l3")}</li>
        <li>{t("partnership.s3l4")}</li>
        <li>{t("partnership.s3l5")}</li>
      </ul>

      <SectionHeading t={t} k="partnership.s4Title" as={lv.section} />
      <p>{t("partnership.s4p1")}</p>
      <ul>
        <li>{t("partnership.s4l1")}</li>
        <li>{t("partnership.s4l2")}</li>
        <li>{t("partnership.s4l3")}</li>
        <li>{t("partnership.s4l4")}</li>
      </ul>

      <SectionHeading t={t} k="partnership.s5Title" as={lv.section} />
      <p>{t("partnership.s5p1")}</p>
      <p>{t("partnership.s5p2")}</p>
      <p>{t("partnership.s5p3")}</p>
      <p>{t("partnership.s5p4")}</p>

      <SectionHeading t={t} k="partnership.s5aTitle" as={lv.section} />
      <p>{t("partnership.s5ap1")}</p>
      <ul>
        <li>{t("partnership.s5al1")}</li>
        <li>{t("partnership.s5al2")}</li>
        <li>{t("partnership.s5al3")}</li>
      </ul>
      <p>{t("partnership.s5ap2")}</p>
      <p>{t("partnership.s5ap3")}</p>

      <SectionHeading t={t} k="partnership.s6Title" as={lv.section} />
      <p>{t("partnership.s6p1")}</p>
      <p>{t("partnership.s6p2")}</p>

      <SectionHeading t={t} k="partnership.s7Title" as={lv.section} />
      <p>{t("partnership.s7p1")}</p>
      <p>{t("partnership.s7p2")}</p>

      <SectionHeading t={t} k="partnership.s8Title" as={lv.section} />
      <p>{t("partnership.s8p1")}</p>
      <ul>
        <li>{t("partnership.s8l1")}</li>
        <li>{t("partnership.s8l2")}</li>
        <li>{t("partnership.s8l3")}</li>
      </ul>
      <p>{t("partnership.s8p2")}</p>

      <SectionHeading t={t} k="partnership.s9Title" as={lv.section} />
      <p>{t("partnership.s9p1")}</p>
      <p>{t("partnership.s9p2")}</p>
      <p>{t("partnership.s9p3")}</p>
      <p>{t("partnership.s9p4")}</p>
      <p>{t("partnership.s9p5")}</p>
      <p>{t("partnership.s9p6")}</p>

      <SectionHeading t={t} k="partnership.s10Title" as={lv.section} />
      <p>{t("partnership.s10p1")}</p>
      <p>{t("partnership.s10p2")}</p>
      <ul>
        <li>{t("partnership.s10l1")}</li>
        <li>{t("partnership.s10l2")}</li>
        <li>{t("partnership.s10l3")}</li>
        <li>{t("partnership.s10l4")}</li>
        <li>{t("partnership.s10l5")}</li>
        <li>{t("partnership.s10l6")}</li>
      </ul>
      <p>{t("partnership.s10p3")}</p>
      <ul>
        <li>{t("partnership.s10s1")}</li>
        <li>{t("partnership.s10s2")}</li>
        <li>{t("partnership.s10s3")}</li>
        <li>{t("partnership.s10s4")}</li>
        <li>{t("partnership.s10s5")}</li>
      </ul>
      <p>{t("partnership.s10p4")}</p>
      <p>{t("partnership.s10p5")}</p>

      <SectionHeading t={t} k="partnership.s11Title" as={lv.section} />
      <p>{t("partnership.s11p1")}</p>

      <SectionHeading t={t} k="partnership.s12Title" as={lv.section} />
      <p>{t("partnership.s12p1")}</p>

      <SectionHeading t={t} k="partnership.s13Title" as={lv.section} />
      <p>{t("partnership.s13p1")}</p>
    </article>
  );
}

/* ─────────────────────────── ACCESSIBILITY ─────────────────────────── */

/// Accessibility statement per Dir. (UE) 2019/882 European Accessibility Act
/// (în vigoare 2025-06-28). Pontimed = B2C digital service in scope.
/// See _docs/legal/compliance-framework.md §3.4
export function AccessibilitySection({ t, titleAs, titleClassName }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("accessibility.title")}
      </DocTitle>
      <p className="lead">{t("accessibility.intro")}</p>

      <SectionHeading t={t} k="accessibility.s1Title" as={lv.section} />
      <p>{t("accessibility.s1p1")}</p>
      <p>{t("accessibility.s1p2")}</p>

      <SectionHeading t={t} k="accessibility.s2Title" as={lv.section} />
      <p>{t("accessibility.s2p1")}</p>
      <ul>
        <li>{t("accessibility.s2l1")}</li>
        <li>{t("accessibility.s2l2")}</li>
        <li>{t("accessibility.s2l3")}</li>
        <li>{t("accessibility.s2l4")}</li>
      </ul>

      <SectionHeading t={t} k="accessibility.s3Title" as={lv.section} />
      <p>{t("accessibility.s3p1")}</p>
      <p>{t("accessibility.s3p2")}</p>

      <SectionHeading t={t} k="accessibility.s4Title" as={lv.section} />
      <p>{t("accessibility.s4p1")}</p>
      <p>{t("accessibility.s4p2")}</p>

      <SectionHeading t={t} k="accessibility.s5Title" as={lv.section} />
      <p>{t("accessibility.s5p1")}</p>
    </article>
  );
}

/* ─────────────────────────── DSA NOTICE ─────────────────────────── */

/// DSA Art. 16 notice & action information section.
/// Links to the report-content form page: a button right under the intro (the
/// page's one action) and the original link at the end of §4.
/// See _docs/legal/compliance-framework.md §2.10
export function DsaSection({ t, titleAs, titleClassName, links }: LegalSectionProps) {
  const lv = headingLevels(titleAs);
  const reportContentHref = links?.reportContent ?? DEFAULT_REPORT_CONTENT_URL;
  return (
    <article className={ARTICLE_CLASS}>
      <DocTitle as={titleAs} className={titleClassName}>
        {t("dsa.title")}
      </DocTitle>
      <p className="lead">{t("dsa.intro")}</p>
      <LegalCta href={reportContentHref}>{t("nav.reportContent")}</LegalCta>

      <SectionHeading t={t} k="dsa.s1Title" as={lv.section} />
      <p>{t("dsa.s1p1")}</p>
      <p>{t("dsa.s1p2")}</p>

      <SectionHeading t={t} k="dsa.s2Title" as={lv.section} />
      <p>{t("dsa.s2p1")}</p>
      <ul>
        <li>{t("dsa.s2l1")}</li>
        <li>{t("dsa.s2l2")}</li>
        <li>{t("dsa.s2l3")}</li>
        <li>{t("dsa.s2l4")}</li>
        <li>{t("dsa.s2l5")}</li>
      </ul>

      <SectionHeading t={t} k="dsa.s3Title" as={lv.section} />
      <p>{t("dsa.s3p1")}</p>
      <p>{t("dsa.s3p2")}</p>

      <SectionHeading t={t} k="dsa.s4Title" as={lv.section} />
      <p>{t("dsa.s4p1")}</p>
      <p>
        <a href={reportContentHref}>{t("nav.reportContent")}</a>
      </p>
    </article>
  );
}

/* ─────────────────────────── REGISTRY ─────────────────────────── */

import type { LegalSegment } from "./segments";
export { LEGAL_SEGMENTS, LEGAL_NAV_KEYS, isLegalSegment, type LegalSegment } from "./segments";

/**
 * One legal document by segment. `options` is optional so existing two-argument
 * callers (apps/app legal sheet, apps/web legal modal) render exactly as before.
 */
export function renderLegalSection(
  id: LegalSegment,
  t: LegalTranslator,
  options: LegalSectionOptions = {},
) {
  switch (id) {
    case "terms":
      return <TermsSection t={t} {...options} />;
    case "privacy":
      return <PrivacySection t={t} {...options} />;
    case "cookies":
      return <CookiesSection t={t} {...options} />;
    case "gdpr":
      return <GdprSection t={t} {...options} />;
    case "security":
      return <SecuritySection t={t} {...options} />;
    case "ehds":
      return <EhdsSection t={t} {...options} />;
    case "data-protection":
      return <DataProtectionSection t={t} {...options} />;
    case "data-deletion":
      return <DataDeletionSection t={t} {...options} />;
    case "listed-professionals":
      return <ListedProfessionalsSection t={t} {...options} />;
    case "accessibility":
      return <AccessibilitySection t={t} {...options} />;
    case "dsa":
      return <DsaSection t={t} {...options} />;
    case "partnership":
      return <PartnershipSection t={t} {...options} />;
    default:
      return null;
  }
}
